import {Box, Typography} from "@mui/material";
import {useState} from "react";
import {c, softShadow} from "../landingTokens";
import {Eyebrow, Section} from "./Shell";

const FAQS = [
  {
    q: "Do we have to replace our phone system?",
    a: "No. Your Avaya or existing phone central trunks into our Asterisk over standard SIP. Your numbers, routing and agent desktops stay exactly as they are — the AI simply becomes one more destination calls can reach.",
  },
  {
    q: "How does the AI actually hear the call?",
    a: "Through our custom SIP connector, which streams the live audio to and from the agent over AudioSocket. That direct audio path is what keeps the response under a second, with no menus or beeps in between.",
  },
  {
    q: "Can we change how the agent behaves?",
    a: "Yes — the conversation logic is a flow you design in the OpenBrain app. Prompts, branches, guardrails and escalation rules are edited there and take effect without a software release.",
  },
  {
    q: "How does it reach our own systems?",
    a: "The flow calls your APIs through MCP tools and REST endpoints, so it can check a balance, reschedule an appointment or open a ticket while the caller is still on the line.",
  },
  {
    q: "Can a supervisor take over a live call?",
    a: "Yes. The supervision interface lists every conversation in progress, and a supervisor can stop the AI mid-call and redirect the caller to an Avaya phone, or place an outbound call to their own Avaya extension directly from the interface.",
  },
  {
    q: "Is every conversation logged and reviewable?",
    a: "Every call is logged with its full transcript and the path it took through the flow. Supervisors leave comments on specific turns, and those notes become the change list for the next iteration of the flow — the agent gets better from real traffic, not guesswork.",
  },
  {
    q: "Which languages are supported?",
    a: "English, Macedonian, Serbian and Croatian are live today, and we can train models for any additional language you need. A single agent can switch language mid-call based on the caller.",
  },
  {
    q: "Where is call data stored?",
    a: "EU cloud by default, with on-premise deployment available. Recordings and transcripts support redaction of sensitive fields and full audit logging under GDPR.",
  },
  {
    q: "How long does a rollout take?",
    a: "Typically three to six weeks: one week of discovery and intent mapping, a shadow-mode pilot on real traffic, then a gradual switch of call volume to the agent.",
  },
];

export const Faq = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
  <Section id="faq" label="FAQ">
    <Box sx={{maxWidth: 820, mx: "auto"}}>
      <Eyebrow>FAQ</Eyebrow>
      <Typography component="h2" sx={{fontSize: "clamp(30px, 4.2vw, 50px)", lineHeight: 1.05, letterSpacing: "-0.03em", fontWeight: 600, mb: 1.5}}>
        Questions, answered.
      </Typography>
      <Typography sx={{fontSize: 17, lineHeight: 1.5, color: c.inkSoft, maxWidth: 560, mb: 5}}>
        The things technical teams ask us most, before a pilot.
      </Typography>

      <Box className="reveal-stagger" sx={{bgcolor: c.white, borderRadius: "28px", boxShadow: softShadow, px: {xs: 2.5, md: 4}, py: {xs: 1, md: 1.5}}}>
        {FAQS.map((item, index) => {
          const open = openIndex === index;
          return (
            <Box
              key={item.q}
              sx={{
                borderTop: index === 0 ? "none" : `1px solid rgba(124,108,246,0.14)`,
                py: {xs: 2.25, md: 2.75},
              }}
            >
              <Box
                component="button"
                type="button"
                aria-expanded={open}
                onClick={() => setOpenIndex(open ? -1 : index)}
                sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, width: "100%", p: 0, border: 0, bgcolor: "transparent", color: c.ink, textAlign: "left", cursor: "pointer", font: "inherit", "&:hover h3": {color: c.purpleDeep}}}
              >
                <Typography component="h3" sx={{fontSize: {xs: 15.5, md: 17}, fontWeight: 600, letterSpacing: "-0.01em", transition: "color 300ms cubic-bezier(.16,1,.3,1)"}}>{item.q}</Typography>
                {/* Soft round toggle chip. */}
                <Box
                  aria-hidden
                  sx={{
                    flexShrink: 0,
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: open ? c.purpleDeep : "rgba(124,108,246,0.1)",
                    color: open ? "#fff" : c.purpleDeep,
                    fontSize: 19,
                    lineHeight: 1,
                    transform: open ? "rotate(45deg)" : "none",
                    transition: "transform 400ms cubic-bezier(.16,1,.3,1), background-color 300ms ease, color 300ms ease",
                  }}
                >
                  +
                </Box>
              </Box>
              <Box sx={{maxHeight: open ? 240 : 0, opacity: open ? 1 : 0, overflow: "hidden", transition: "max-height 450ms cubic-bezier(.16,1,.3,1), opacity 300ms ease"}}>
                <Typography sx={{fontSize: 14.5, lineHeight: 1.6, color: c.inkSoft, pt: 1.5, pr: {xs: 1, md: 5}}}>{item.a}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  </Section>
  );
};
